import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, User as UserIcon, Phone, AtSign, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useBrandName } from "@/context/BrandingContext";
import { DynictyAuthShell } from "@/components/auth/DynictyAuthShell";
import { PasswordInput } from "@/components/auth/PasswordInput";

const LABEL_CLS = "text-xs font-medium";
const INPUT_CLS = "dyn-auth-input pl-9";
const ICON_CLS = "absolute left-3 top-1/2 -translate-y-1/2 dyn-auth-icon";

export function LoginPage() {
  const { login } = useAuth();
  const brandName = useBrandName();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();
  const next = new URLSearchParams(loc.search).get("next") || "/";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(identifier.trim(), password);
      toast.success("Selamat datang kembali!");
      if (u.role === "admin" || u.role === "owner") navigate("/admin");
      else navigate(next);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  return (
    <DynictyAuthShell
      tab="login"
      mascot="welcome"
      title="Selamat Datang Kembali"
      subtitle={<>Masuk ke akun <span className="font-medium" style={{ color: "#67e8f9" }} data-testid="auth-tenant-name">{brandName}</span> Anda</>}
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className={LABEL_CLS}>Username atau Email</Label>
          <div className="relative mt-1">
            <AtSign className={ICON_CLS} size={16} />
            <Input
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              type="text"
              autoComplete="username"
              required
              placeholder="Masukkan username atau email"
              className={INPUT_CLS}
              data-testid="login-identifier"
            />
          </div>
        </div>
        <div>
          <Label className={LABEL_CLS}>Password</Label>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            placeholder="Masukkan password Anda"
            testId="login-password"
          />
        </div>
        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-xs dyn-auth-link" data-testid="forgot-password-link">Lupa password?</Link>
        </div>
        <Button type="submit" disabled={loading} className="w-full dyn-auth-btn" data-testid="login-submit">
          {loading ? "Memproses..." : <span className="inline-flex items-center gap-2">Masuk <ArrowRight size={16} /></span>}
        </Button>
        <div className="text-center text-sm dyn-auth-muted">
          Belum punya akun? <Link to="/register" className="dyn-auth-link" data-testid="goto-register">Daftar gratis</Link>
        </div>
      </form>
    </DynictyAuthShell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const [form, setForm] = useState({ username: "", name: "", email: "", phone: "", password: "", confirm_password: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // Client-side validation
    const username = form.username.trim().toLowerCase();
    if (!/^[a-zA-Z0-9._]{4,30}$/.test(username)) {
      toast.error("Username hanya boleh huruf, angka, titik, underscore (min 4 karakter, tanpa spasi)");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    if (form.password !== form.confirm_password) {
      toast.error("Password dan konfirmasi password tidak sama");
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, username, email: form.email.trim().toLowerCase() });
      toast.success("Akun berhasil dibuat!");
      navigate("/");
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  return (
    <DynictyAuthShell tab="register" mascot="register" title="Daftar Akun Baru" subtitle="Buat akun dan nikmati promo eksklusif">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label className={LABEL_CLS}>Username</Label>
          <div className="relative mt-1">
            <AtSign className={ICON_CLS} size={16} />
            <Input
              value={form.username}
              onChange={(e) => upd("username", e.target.value.replace(/\s/g, ""))}
              required
              minLength={4}
              maxLength={30}
              autoComplete="username"
              placeholder="contoh: johndoe_88"
              className={INPUT_CLS}
              data-testid="register-username"
            />
          </div>
          <div className="text-[11px] dyn-auth-hint mt-1">Huruf, angka, titik, underscore (4–30 karakter, tanpa spasi)</div>
        </div>
        <div>
          <Label className={LABEL_CLS}>Nama Lengkap</Label>
          <div className="relative mt-1">
            <UserIcon className={ICON_CLS} size={16} />
            <Input value={form.name} onChange={(e) => upd("name", e.target.value)} required className={INPUT_CLS} data-testid="register-name" />
          </div>
        </div>
        <div>
          <Label className={LABEL_CLS}>Email</Label>
          <div className="relative mt-1">
            <Mail className={ICON_CLS} size={16} />
            <Input value={form.email} onChange={(e) => upd("email", e.target.value)} type="email" autoComplete="email" required className={INPUT_CLS} data-testid="register-email" />
          </div>
        </div>
        <div>
          <Label className={LABEL_CLS}>No. WhatsApp</Label>
          <div className="relative mt-1">
            <Phone className={ICON_CLS} size={16} />
            <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="08xxxx" className={INPUT_CLS} data-testid="register-phone" />
          </div>
        </div>
        <div>
          <Label className={LABEL_CLS}>Password</Label>
          <PasswordInput value={form.password} onChange={(e) => upd("password", e.target.value)} autoComplete="new-password" required minLength={6} testId="register-password" />
          <div className="text-[11px] dyn-auth-hint mt-1">Min. 6 karakter</div>
        </div>
        <div>
          <Label className={LABEL_CLS}>Konfirmasi Password</Label>
          <PasswordInput value={form.confirm_password} onChange={(e) => upd("confirm_password", e.target.value)} autoComplete="new-password" required minLength={6} testId="register-confirm-password" />
          {form.confirm_password && form.password !== form.confirm_password && (
            <div className="text-[11px] mt-1" style={{ color: "#f87171" }}>Password tidak sama</div>
          )}
        </div>
        <Button type="submit" disabled={loading} className="w-full dyn-auth-btn mt-2" data-testid="register-submit">
          {loading ? "Memproses..." : "Daftar Sekarang"}
        </Button>
        <div className="text-center text-sm dyn-auth-muted">
          Sudah punya akun? <Link to="/login" className="dyn-auth-link" data-testid="goto-login">Masuk</Link>
        </div>
      </form>
    </DynictyAuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [token, setToken] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { default: api } = await import("@/lib/api");
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data.dev_token) setToken(data.dev_token);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <DynictyAuthShell mascot="reset" title="Lupa Password" subtitle="Masukkan email akun Anda">
      {sent ? (
        <div className="text-center space-y-3">
          <div className="text-sm dyn-auth-muted">Jika email terdaftar, link reset password telah dikirim.</div>
          {token && (
            <div className="dyn-auth-soft rounded-xl p-3 text-xs text-left">
              <div className="font-bold mb-1">[Dev Mode] Token reset:</div>
              <code className="break-all">{token}</code>
              <div className="mt-2"><Link to={`/reset-password?token=${token}`} className="dyn-auth-link">Klik untuk reset</Link></div>
            </div>
          )}
          <Link to="/login" className="text-sm dyn-auth-link">Kembali ke Login</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="relative">
            <Mail className={ICON_CLS} size={16} />
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="Email" className={INPUT_CLS} data-testid="forgot-email" />
          </div>
          <Button type="submit" className="w-full dyn-auth-btn" data-testid="forgot-submit">Kirim Link Reset</Button>
          <Link to="/login" className="block text-center text-sm dyn-auth-link">Kembali ke Login</Link>
        </form>
      )}
    </DynictyAuthShell>
  );
}

export function ResetPasswordPage() {
  const loc = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(loc.search).get("token") || "";
  const [pw, setPw] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { default: api } = await import("@/lib/api");
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <DynictyAuthShell mascot="reset" title="Reset Password" subtitle="Buat password baru Anda">
      {done ? (
        <div className="text-center text-sm" style={{ color: "#4ade80" }}>Password berhasil direset. Mengarahkan ke login...</div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} minLength={6} required placeholder="Password baru" testId="reset-password" />
          <Button type="submit" className="w-full dyn-auth-btn" data-testid="reset-submit">Reset Password</Button>
        </form>
      )}
    </DynictyAuthShell>
  );
}
